import { CONTACT_LINK_CATEGORIES, CONTACT_LINK_CATEGORIES_ENHANCED, getLinkMetadata } from '../constants/contactLinkCategories';

/**
 * Enhanced function to get contact link category with backward compatibility
 * @param {string} type - The contact link type
 * @param {boolean} useEnhanced - Whether to use enhanced category mapping (default: false for backward compatibility)
 * @returns {string} The category name
 */
export function getContactLinkCategory(type, useEnhanced = false) {
    if (!type) return 'Other';

    const normalizedType = type.toLowerCase().trim();

    // Enhanced category mapping with more detailed categories
    if (useEnhanced) {
        const metadata = getLinkMetadata(normalizedType);
        if (metadata) {
            return metadata.category.charAt(0).toUpperCase() + metadata.category.slice(1);
        }

        // Fallback to basic category mapping
        return getBasicCategory(normalizedType);
    }

    // Backward compatibility: Use the original simple array structure
    return getBasicCategory(normalizedType);
}

/**
 * Get basic category using the original simple array structure
 * @param {string} normalizedType - Normalized link type
 * @returns {string} Category name
 */
function getBasicCategory(normalizedType) {
    if (CONTACT_LINK_CATEGORIES.social.includes(normalizedType)) {
        return 'Social Media';
    }

    if (CONTACT_LINK_CATEGORIES.products.includes(normalizedType)) {
        return 'Products & Services';
    }

    if (CONTACT_LINK_CATEGORIES.business.includes(normalizedType)) {
        return 'Business';
    }

    if (CONTACT_LINK_CATEGORIES.personal.includes(normalizedType)) {
        return 'Contact';
    }

    return 'Other';
}

/**
 * Get detailed category information including metadata
 * @param {string} type - The contact link type
 * @returns {Object|null} Detailed category information or null if not found
 */
export function getContactLinkCategoryDetails(type) {
    if (!type) return null;

    const normalizedType = type.toLowerCase().trim();
    const metadata = getLinkMetadata(normalizedType);

    if (!metadata) return null;

    return {
        name: metadata.name,
        category: metadata.category,
        displayName: metadata.category.charAt(0).toUpperCase() + metadata.category.slice(1),
        icon: metadata.icon,
        color: metadata.color,
        description: metadata.description,
        enabled: metadata.enabled,
        urlPattern: metadata.urlPattern,
        validation: metadata.validation
    };
}

/**
 * Check if a contact link type is valid
 * @param {string} type - The contact link type to validate
 * @returns {boolean} True if valid, false otherwise
 */
export function isValidContactLinkType(type) {
    if (!type || typeof type !== 'string') return false;

    const normalizedType = type.toLowerCase().trim();
    return getLinkMetadata(normalizedType) !== null;
}

/**
 * Get all available contact link types for a specific category
 * @param {string} category - The category name (social, products, business, personal)
 * @returns {Array} Array of link types in the category
 */
export function getContactLinkTypesByCategory(category) {
    if (!category || typeof category !== 'string') return [];

    const normalizedCategory = category.toLowerCase().trim();

    if (CONTACT_LINK_CATEGORIES[normalizedCategory]) {
        return CONTACT_LINK_CATEGORIES[normalizedCategory];
    }

    return [];
}

/**
 * Get all categories with their display names
 * @returns {Array} Array of category objects with name and displayName
 */
export function getContactLinkCategories() {
    return [
        { name: 'social', displayName: 'Social Media' },
        { name: 'products', displayName: 'Products & Services' },
        { name: 'business', displayName: 'Business' },
        { name: 'personal', displayName: 'Contact' }
    ];
}

/**
 * Enhanced function to categorize contact links with additional context
 * @param {Array} contactLinks - Array of contact link objects
 * @param {boolean} useEnhanced - Whether to use enhanced categorization
 * @returns {Object} Categorized contact links
 */
export function categorizeContactLinks(contactLinks, useEnhanced = false) {
    if (!Array.isArray(contactLinks)) return {};

    const categorized = {
        'Social Media': [],
        'Products & Services': [],
        'Business': [],
        'Contact': [],
        'Other': []
    };

    contactLinks.forEach(link => {
        const category = getContactLinkCategory(link.type, useEnhanced);
        if (categorized[category]) {
            categorized[category].push(link);
        } else {
            categorized['Other'].push(link);
        }
    });

    // Remove empty categories
    Object.keys(categorized).forEach(category => {
        if (categorized[category].length === 0) {
            delete categorized[category];
        }
    });

    return categorized;
}

/**
 * Get contact link statistics
 * @param {Array} contactLinks - Array of contact link objects
 * @returns {Object} Statistics about the contact links
 */
export function getContactLinkStats(contactLinks) {
    if (!Array.isArray(contactLinks)) return {};

    const stats = {
        total: contactLinks.length,
        byCategory: {},
        byType: {},
        visible: 0,
        enabled: 0
    };

    contactLinks.forEach(link => {
        const category = getContactLinkCategory(link.type);

        // Count by category
        stats.byCategory[category] = (stats.byCategory[category] || 0) + 1;

        // Count by type
        stats.byType[link.type] = (stats.byType[link.type] || 0) + 1;

        // Count visible links
        if (link.isVisible) {
            stats.visible++;
        }

        // Count enabled links (if enhanced data is available)
        const metadata = getLinkMetadata(link.type.toLowerCase());
        if (metadata && metadata.enabled) {
            stats.enabled++;
        }
    });

    return stats;
}

// Export the original function for backward compatibility
export default getContactLinkCategory; 